import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const INDUSTRIES = ['Technology','Finance','Healthcare','Education','E-Commerce','Media','Consulting','Other'];
const SIZES = ['1-10','11-50','51-200','201-500','500+'];

export default function TenantOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { switchTenant } = useTenant();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', industry: '', employee_count: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Business name is required'); return; }
    setLoading(true);
    try {
      const res = await api.post('/tenants', form);
      switchTenant(res.data, 'business_admin');
      toast.success(`Welcome to ${res.data.name}! Your 14-day trial has started.`);
      navigate('/tenant');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create tenant');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 520, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 40 }}>
        {/* Progress */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
          {[1, 2].map(s => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 4, background: s <= step ? 'var(--accent)' : 'var(--border)' }} />
          ))}
        </div>

        {step === 1 && (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Set up your workspace</h1>
            <p style={{ color: 'var(--text-2)', marginBottom: 28 }}>Tell us about your business to personalise your experience.</p>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Business name *</span>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme Corp" className="input" style={{ width: '100%' }} />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Industry</span>
              <select value={form.industry} onChange={e => set('industry', e.target.value)} className="input" style={{ width: '100%' }}>
                <option value="">Select industry</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </label>
            <label style={{ display: 'block', marginBottom: 28 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Team size</span>
              <select value={form.employee_count} onChange={e => set('employee_count', e.target.value)} className="input" style={{ width: '100%' }}>
                <option value="">Select size</option>
                {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
              </select>
            </label>
            <button onClick={() => setStep(2)} disabled={!form.name.trim()} className="btn btn-primary" style={{ width: '100%' }}>Continue</button>
          </>
        )}

        {step === 2 && (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>You're all set, {user?.name?.split(' ')[0]}!</h1>
            <p style={{ color: 'var(--text-2)', marginBottom: 28 }}>Your workspace <strong>{form.name}</strong> will start with a 14-day free trial. No credit card required.</p>
            <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, marginBottom: 28 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>What's included in your trial</h3>
              {['API Testing & Mock Server','Team Management (up to 15 members)','Project Analytics','Code Snippet Generator','14-day full access to Professional plan'].map(f => (
                <div key={f} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: '#a6e3a1' }}>✓</span>
                  <span style={{ color: 'var(--text-2)' }}>{f}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} className="btn btn-ghost" style={{ flex: 1 }}>Back</button>
              <button onClick={handleSubmit} disabled={loading} className="btn btn-primary" style={{ flex: 2 }}>
                {loading ? 'Creating workspace...' : 'Create workspace →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
